import { Sparkles, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCountUp } from "@/hooks/use-count-up";


export interface SlotsInfo {
  claimed: number;
  total: number;
  remaining: number;
}

/**
 * Displays the "first 50 verified users get free plan" counter.
 * Compact variant renders a small pill; full renders a progress card.
 */
export function FreePlanSlotsBadge({
  slots,
  variant = "card",
  className = "",
}: {
  slots: SlotsInfo | null | undefined;
  variant?: "card" | "pill";
  className?: string;
}) {
  const { t } = useTranslation();
  const claimed = slots?.claimed ?? 0;
  const total = slots?.total ?? 50;
  const remaining = slots?.remaining ?? Math.max(0, total - claimed);
  const pct = Math.min(100, Math.round((claimed / Math.max(1, total)) * 100));
  const animatedClaimed = useCountUp(claimed);
  const isFull = remaining <= 0;
  const scarce = remaining > 0 && remaining <= 10;


  if (variant === "pill") {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium backdrop-blur-sm ${
          isFull
            ? "border-destructive/40 bg-destructive/10 text-destructive"
            : scarce
              ? "border-amber-400/40 bg-amber-400/10 text-amber-400"
              : "border-emerald-400/40 bg-emerald-400/10 text-emerald-400"
        } ${className}`}
      >
        <span className="relative flex h-1.5 w-1.5">
          {!isFull && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
          )}
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-current" />
        </span>
        <Sparkles className="h-3 w-3" />
        {isFull
          ? t("slotsBadge.pillFull", { total })
          : t("slotsBadge.pillClaimed", { claimed: Math.round(animatedClaimed), total })}

      </span>
    );
  }

  return (
    <div
      className={`rounded-xl border p-3 text-start ${
        isFull
          ? "border-destructive/30 bg-destructive/5"
          : scarce
            ? "border-amber-400/30 bg-amber-400/5"
            : "border-emerald-400/25 bg-emerald-400/5"
      } ${className}`}
    >
      <div className="flex items-center justify-between gap-2 text-[11px] font-medium uppercase tracking-wider">
        <span
          className={`inline-flex items-center gap-1 ${
            isFull ? "text-destructive" : scarce ? "text-amber-400" : "text-emerald-400"
          }`}
        >
          <Users className="h-3 w-3" />
          {isFull ? t("slotsBadge.chipFull") : t("slotsBadge.chipLimited")}
        </span>
        <span className="tabular-nums text-muted-foreground">
          {isFull ? `0 / ${total}` : t("slotsBadge.leftCount", { count: remaining })}
        </span>

      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-all duration-700 ${
            isFull
              ? "bg-destructive"
              : scarce
                ? "bg-gradient-to-r from-amber-400 to-orange-500"
                : "bg-gradient-to-r from-emerald-400 to-primary"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1.5 text-[11px] text-muted-foreground">
        {isFull
          ? t("slotsBadge.cardFull", { total })
          : t("slotsBadge.cardCopy", { claimed: Math.round(animatedClaimed), total })}
      </div>

    </div>
  );
}
